import { useSettings } from '@/data/Settings';
import { usePathname } from 'next/navigation';
import { sendAdobeTargetView } from 'presentation/events/hooks/adobe/utils';
import { useEffect, useRef } from 'react';
import {
	ADOBE_NOT_ALLOWED_PATH_LIST_KEY,
	ADOBE_TARGET_ENTITY_VIEWS_ENABLED_KEY,
	ENABLE_ADOBE_SCRIPT_FLAG,
} from 'shared/custom/constants/commonConstants';
import { isScriptAllowedForPath } from 'shared/custom/utils/scriptPathFilter';

/**
 * Fires an Adobe Target view (WebSDK `sendEvent` with a `viewName`) scoped to a
 * single entity (a specific product or a specific category/sub-category/department),
 * so marketers can target A/B and XT tests at one PDP or PLP instead of every page
 * of that type. Coexists with `useAdobeTargetPageView`'s generic pathname-based view.
 *
 * The caller builds the `viewName` (e.g. via `buildProductEntityViewName` /
 * `buildCategoryEntityViewName` in ./utils) from data it already has in scope, so
 * this hook fires no additional requests of its own. It re-fires whenever `viewName`
 * changes, since SPA navigation between two entities of the same type doesn't remount
 * the calling component.
 *
 * Storeconf: set userData `ADOBE_TARGET_ENTITY_VIEWS_ENABLED` to `'true'` to enable
 * this hook. Disabled by default while the value doesn't exist. Also short-circuits
 * globally when `ENABLE_ADOBE_SCRIPT_FLAG` isn't `'true'`.
 */
export const useAdobeTargetEntityView = (viewName: string | undefined) => {
	const prevViewNameRef = useRef<string | undefined>(undefined);
	const { settings } = useSettings();
	const pathname = usePathname();

	useEffect(() => {
		try {
			const isAdobeScriptEnabled = settings?.userData?.[ENABLE_ADOBE_SCRIPT_FLAG] === 'true';
			if (!isAdobeScriptEnabled) return;

			if (viewName === undefined) return;
			if (viewName === prevViewNameRef.current) return;

			const isEnabled = settings?.userData?.[ADOBE_TARGET_ENTITY_VIEWS_ENABLED_KEY] === 'true';
			if (!isEnabled) return;

			const isAllowed = isScriptAllowedForPath(
				settings?.userData,
				pathname ?? undefined,
				ADOBE_NOT_ALLOWED_PATH_LIST_KEY
			);
			if (!isAllowed) return;

			prevViewNameRef.current = viewName;
			sendAdobeTargetView(viewName);
		} catch (e) {
			console.error('Error sending Adobe Target entity view', e);
		}
	}, [viewName, settings?.userData, pathname]);
};
